import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Descriptions, Input, Select, Space, Table, message } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { LocationLabel } from "../../components/LocationLabel";
import { LineItemsEditor, type LineItem } from "../../components/LineItemsEditor";
import { useApi } from "../../helpers/useApi";
import { fmtLocation } from "../../helpers/format";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useQuarantineLocationsQuery, useStockLocationsQuery } from "../../helpers/queries";
import { ADJUSTMENT_TYPES, isQuarantineAdjustment } from "./types";
import type { StockAdjustment } from "../../api-models";

// Damage, wastage, loss and physical-count corrections are all one approved document
// (spec §18/§31.13) — nothing posts to the ledger until it is approved, and a Central
// Warehouse adjustment cannot be approved by the person who raised it (spec §30.2).
export default function AdjustmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: locations = [] } = useStockLocationsQuery();

  const isNew = id === "new";
  // The Site Stock tab links straight here with the store (or its quarantine) and the
  // intended type already picked, so the PM lands on a form that only needs items.
  const [searchParams] = useSearchParams();
  const [adj, setAdj] = useState<StockAdjustment | null>(null);
  const [items, setItems] = useState<LineItem[]>([]);
  const [locationId, setLocationId] = useState<number | undefined>(() => {
    const v = Number(searchParams.get("locationId"));
    return Number.isFinite(v) && v > 0 ? v : undefined;
  });
  const [type, setType] = useState<string>(() => {
    const v = searchParams.get("type");
    return ADJUSTMENT_TYPES.some((t) => t.value === v) ? v! : "DAMAGE";
  });
  const [reason, setReason] = useState("");

  const quarantineType = isQuarantineAdjustment(type);
  // Quarantine stores are only fetched once a quarantine type is actually selected.
  const { data: quarantineLocations = [] } = useQuarantineLocationsQuery(isNew && quarantineType);
  const locationOptions = quarantineType ? quarantineLocations : locations;

  async function load() {
    if (isNew) return;
    const res = await request<StockAdjustment>({ url: `/adjustments/${id}` });
    setAdj(res);
    setItems((res.lines ?? []).map((l) => ({ key: String(l.id), itemId: l.itemId, qty: l.qty })));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canWrite = hasAnyGrant(user, "Adjustments.Write");
  const docType = adj?.siteId ? "STOCK_ADJUSTMENT_SITE" : "STOCK_ADJUSTMENT_WAREHOUSE";
  const selectedType = ADJUSTMENT_TYPES.find((t) => t.value === (adj?.type ?? type));

  async function create() {
    const created = await request<StockAdjustment>({
      method: "POST",
      url: "/adjustments",
      data: {
        locationId,
        type,
        reason,
        items: items.filter((it) => it.itemId && it.qty).map((it) => ({ itemId: it.itemId, qty: it.qty })),
      },
    });
    message.success("Adjustment created.");
    navigate(`/adjustments/${created.id}`, { replace: true });
  }

  async function act(action: string) {
    await request({ method: "POST", url: `/adjustments/${id}/${action}` });
    message.success("Done.");
    load();
  }

  if (!isNew && !adj) return null;

  return (
    <div>
      <PageHeader
        title={isNew ? "New Stock Adjustment" : adj!.docNo}
        extra={
          <Space wrap>
            {adj && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={adj.status} />
              </span>
            )}
            {isNew && canWrite && (
              <Button type="primary" onClick={create} loading={loading} disabled={!locationId || items.length === 0} data-tour="adjustment-create">
                Save
              </Button>
            )}
            {!isNew && adj?.status === "DRAFT" && canWrite && (
              <Button onClick={() => act("submit")} data-tour="adjustment-submit">
                Submit for approval
              </Button>
            )}
            {!isNew && (adj?.status === "DRAFT" || adj?.status === "PENDING_APPROVAL") && canWrite && (
              <Button danger onClick={() => act("cancel")}>
                Cancel
              </Button>
            )}
          </Space>
        }
      />

      {selectedType && <Alert type="info" showIcon style={{ marginBottom: 16 }} message={selectedType.help} />}
      {isNew && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Nothing moves until this is approved"
          description="A Central Warehouse adjustment goes to the configured warehouse approver (Management by default) and cannot be approved by whoever raised it."
        />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Location">
            {isNew ? (
              <Select
                data-tour="adjustment-location"
                style={{ width: 280 }}
                showSearch
                optionFilterProp="label"
                placeholder={quarantineType ? "Which quarantine store?" : "Where is the stock?"}
                options={locationOptions.map((l) => ({ value: l.id, label: fmtLocation(l) }))}
                value={locationOptions.some((l) => l.id === locationId) ? locationId : undefined}
                onChange={setLocationId}
              />
            ) : (
              <LocationLabel location={adj!.location} fallback="-" />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            {isNew ? (
              <Select
                data-tour="adjustment-type"
                style={{ width: 240 }}
                options={ADJUSTMENT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                value={type}
                onChange={(v) => {
                  // A store and a quarantine store come from two different lists, so
                  // switching families has to drop the selection rather than carry an id
                  // the server would reject.
                  if (isQuarantineAdjustment(v) !== quarantineType) setLocationId(undefined);
                  setType(v);
                }}
              />
            ) : (
              adj!.type
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Reason" span={2}>
            {isNew ? (
              <Input.TextArea
                data-tour="adjustment-reason"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Justification — required, and kept in the audit trail."
              />
            ) : (
              adj!.reason || "—"
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title={type === "CORRECTION" || adj?.type === "CORRECTION" ? "Items (signed: + found, − missing)" : "Items"}>
        {isNew ? (
          <LineItemsEditor items={items} onChange={setItems} readOnly={!canWrite} />
        ) : (
          <Table
            rowKey="id"
            dataSource={adj?.lines ?? []}
            pagination={false}
            columns={[
              { title: "Code", render: (_, r) => r.item?.code },
              { title: "Item", render: (_, r) => r.item?.name },
              { title: "Qty", dataIndex: "qty" },
              { title: "Posted txn", render: (_, r) => r.stockTxnId ?? "— (not posted)" },
            ]}
          />
        )}
      </Card>

      {!isNew && <ApprovalTimeline docType={docType} docId={Number(id)} refreshKey={adj?.status} onActed={load} />}
    </div>
  );
}
